import { useState } from "react";
import { BellRing, Copy, Plus, QrCode, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PANEL, inputCls } from "./shared";

const CAMPAIGN_TYPES = ["Magazine Ad", "Event", "Social Post", "Flyer / Signage", "Radio / TV", "Other"];
const PARTNER_TYPES = ["Attorney", "Realtor", "Mortgage Professional", "Tax Professional", "Church", "Funeral Home", "Community Organization", "Other"];
const EMPTY_FORM = { name: "", code: "", org_type: "Attorney", kind: "partner", email: "" };
const pillCls = "inline-flex items-center gap-1.5 text-xs font-bold text-navy border border-sand rounded-full px-3 py-1.5 hover:border-gold transition-colors";

function PartnerForm({ onAdded }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const isCampaign = form.kind === "campaign";
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/partners", form);
      toast.success(`"${form.name}" added${form.email ? " — welcome email on its way" : ""}.`);
      setForm({ ...EMPTY_FORM, kind: form.kind, org_type: isCampaign ? "Magazine Ad" : "Attorney" });
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not add partner.");
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-2.5 mb-5" data-testid="partner-form">
      <select className={inputCls} value={form.kind} onChange={(e) => set({ kind: e.target.value, org_type: e.target.value === "campaign" ? "Magazine Ad" : "Attorney" })} data-testid="partner-kind-select">
        <option value="partner">Referral Partner</option>
        <option value="campaign">Marketing Campaign</option>
      </select>
      <input placeholder={isCampaign ? "Name (e.g. Spring Magazine Ad)" : "Partner name (e.g. Johnson Law Firm)"} required className={inputCls} value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="partner-name-input" />
      <input placeholder="Code (e.g. JOHNSONLAW)" required className={inputCls} value={form.code} onChange={(e) => set({ code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })} data-testid="partner-code-input" />
      <select className={inputCls} value={form.org_type} onChange={(e) => set({ org_type: e.target.value })} data-testid="partner-type-select">
        {(isCampaign ? CAMPAIGN_TYPES : PARTNER_TYPES).map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      {!isCampaign && (
        <input type="email" placeholder="Partner email — we'll send their link & QR card (optional)" className={`${inputCls} col-span-2`} value={form.email} onChange={(e) => set({ email: e.target.value })} data-testid="partner-email-input" />
      )}
      <button type="submit" className="col-span-2 min-h-[46px] rounded-xl bg-navy text-cream font-bold text-sm hover:bg-navy-light transition-colors inline-flex items-center justify-center gap-2" data-testid="partner-add-button">
        <Plus className="w-4 h-4" /> {isCampaign ? "Add Campaign" : "Add Partner"}
      </button>
    </form>
  );
}

function PartnerCard({ partner: p, onDelete }) {
  const copyLink = () => {
    navigator.clipboard?.writeText(p.link);
    toast.success("Tracked link copied.");
  };
  return (
    <div className="border border-sand rounded-2xl p-4 flex gap-4 items-start" data-testid="partner-card">
      <img src={`/api/admin/partners/${p.code}/qr.png`} alt={`QR code for ${p.name}`} className="w-20 h-20 rounded-lg border border-sand shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-bold text-navy">
          {p.name}{" "}
          <span className={`ml-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${p.kind === "campaign" ? "text-navy-light bg-cream-dark border-sand" : "text-gold bg-gold-light border-sand"}`}>{p.kind === "campaign" ? "Campaign" : p.org_type}</span>
          {p.kind === "campaign" && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{p.org_type}</span>}
          {p.welcome_sent_at && <span className="ml-1 text-[10px] font-bold text-[#15803D]">✓ welcomed</span>}
        </p>
        <p className="text-xs text-slate-500 font-mono break-all mt-1">{p.link}</p>
        <p className="text-xs text-slate-600 mt-1.5"><strong className="text-navy">{p.starts}</strong> starts • <strong className="text-navy">{p.leads}</strong> leads • <strong className="text-navy">{p.appointments}</strong> appointments</p>
        <div className="flex flex-wrap gap-2 mt-2.5">
          <button onClick={copyLink} className={pillCls} data-testid="partner-copy-button"><Copy className="w-3.5 h-3.5" /> Copy link</button>
          <a href={`/api/admin/partners/${p.code}/qr.png`} target="_blank" rel="noreferrer" className={pillCls} data-testid="partner-qr-button"><QrCode className="w-3.5 h-3.5" /> Print QR</a>
          <a href={`/api/admin/partners/${p.code}/card`} target="_blank" rel="noreferrer" className={pillCls} data-testid="partner-card-button"><QrCode className="w-3.5 h-3.5" /> Print Card</a>
          <button onClick={() => onDelete(p.code)} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 border border-sand rounded-full px-3 py-1.5 hover:text-[#B45309] transition-colors" data-testid="partner-delete-button"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
        </div>
      </div>
    </div>
  );
}

export function PartnersPanel({ partners, onRefresh }) {
  const deletePartner = async (code) => {
    try {
      await api.delete(`/admin/partners/${code}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not remove partner.");
    }
    onRefresh();
  };
  return (
    <div className={PANEL} data-testid="admin-partners">
      <h2 className="font-serif text-2xl font-semibold text-navy mb-1.5 flex items-center gap-2">
        <QrCode className="w-5 h-5 text-gold" /> Referral Partners & QR Codes
      </h2>
      <p className="text-sm text-slate-500 mb-4">Each partner or campaign gets a unique tracked link and a ready-to-print QR code. Add a partner's email and we'll send it to them automatically.</p>
      <PartnerForm onAdded={onRefresh} />
      <div className="space-y-3">
        {partners.map((p) => <PartnerCard key={p.code} partner={p} onDelete={deletePartner} />)}
        {partners.length === 0 && <p className="text-sm text-slate-500">No partners yet — add your first attorney, realtor, or church partner above.</p>}
      </div>
    </div>
  );
}

export function RemindersPanel({ reminders }) {
  const list = reminders?.reminders || [];
  return (
    <div className={PANEL} data-testid="admin-reminders">
      <h2 className="font-serif text-2xl font-semibold text-navy mb-1.5 flex items-center gap-2">
        <BellRing className="w-5 h-5 text-gold" /> Annual Checkup Reminders
      </h2>
      <p className="text-sm text-slate-500 mb-4">Visitors who asked to be invited back next year. Reminder emails send automatically when due.</p>
      <div className="flex gap-3 mb-4">
        <div className="flex-1 bg-cream rounded-xl border border-sand p-3.5 text-center">
          <p className="font-serif text-3xl font-semibold text-navy">{reminders?.total ?? 0}</p>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Opted In</p>
        </div>
        <div className="flex-1 bg-gold-light rounded-xl border border-sand p-3.5 text-center">
          <p className="font-serif text-3xl font-semibold text-navy">{reminders?.due ?? 0}</p>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Due Now</p>
        </div>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {list.map((r) => (
          <div key={r.id} className="flex items-center justify-between text-sm border-b border-sand/60 last:border-0 py-2">
            <span className="font-semibold text-navy">{r.first_name} {r.last_name}</span>
            <span className="text-xs text-slate-500">
              {r.last_reminder_sent_at ? "Last sent " + new Date(r.last_reminder_sent_at).toLocaleDateString() : "Due " + new Date(r.next_reminder_at).toLocaleDateString()}
            </span>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-slate-500">No reminders yet — visitors can opt in on their report page.</p>}
      </div>
    </div>
  );
}
